import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stepper,
  Step,
  StepLabel,
  Box,
  Container,
  Alert
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../context/authContext';
import { useProfile } from '../context/userContext';
import DataSelection from '../components/upgrade/DataSelection';
import OrganizationSetup from '../components/signup/OrganizationSetup';
import PlanSelection from '../components/signup/PlanSelection';
import UpgradeConfirmation from '../components/upgrade/UpgradeConfirmation';
import styles from '../styles/IndividualToOrgUpgrade.module.css';

const IndividualToOrgUpgrade = () => {
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();
  const { authenticated, token } = useAuth();
  const { profile } = useProfile();

  const [activeStep, setActiveStep] = useState(0);
  const [userHasIndividualPlan, setUserHasIndividualPlan] = useState(false);
  const [loading, setLoading] = useState(true);

  const [migrationData, setMigrationData] = useState({
    drones: [],
    missions: [],
    operations: [],
    files: [],
    settings: true
  });

  const [organizationData, setOrganizationData] = useState({
    name: '',
    identifier: '',
    type: 'small_business',
    teamSize: '1-5'
  });

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month');

  const steps = [
    'Select Data to Migrate',
    'Organization Details',
    'Select Plan',
    'Review & Confirm'
  ];

  // Check if user has individual plan
  useEffect(() => {
    if (!authenticated) {
      loginWithRedirect({
        appState: {
          returnTo: '/upgrade/organization'
        }
      });
      return;
    }

    const checkEligibility = async () => {
      try {
        // Check if user has active individual subscription
        if (profile?.subscription?.stripeSubscriptionId) {
          setUserHasIndividualPlan(true);
        } else {
          setUserHasIndividualPlan(false);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    if (profile) {
      checkEligibility();
    }
  }, [authenticated, profile, loginWithRedirect]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleDataSelection = (selectedData) => {
    setMigrationData(selectedData);
    handleNext();
  };

  const handleOrganizationSubmit = (data) => {
    setOrganizationData(data);
    handleNext();
  };

  const handlePlanSelect = (plan, interval) => {
    setSelectedPlan(plan);
    setBillingInterval(interval);
    handleNext();
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DataSelection
            onSubmit={handleDataSelection}
            onCancel={() => navigate('/plans')}
          />
        );
      case 1:
        return (
          <OrganizationSetup
            initialData={organizationData}
            onSubmit={handleOrganizationSubmit}
            onCancel={handleBack}
          />
        );
      case 2:
        return (
          <PlanSelection
            initialPlan={selectedPlan}
            initialInterval={billingInterval}
            teamSize={organizationData.teamSize}
            onSelect={handlePlanSelect}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <UpgradeConfirmation
            migrationData={migrationData}
            organizationData={organizationData}
            selectedPlan={selectedPlan}
            billingInterval={billingInterval}
            currentSubscription={profile?.subscription}
            onBack={handleBack}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  if (!authenticated || loading) {
    return null;
  }

  if (!userHasIndividualPlan) {
    return (
      <Container maxWidth="md" className={styles['upgrade-container']}>
        <Alert severity="warning">
          <strong>No Individual Plan Detected</strong>
          <br />
          You don't currently have an active individual subscription.
          Please <a href="/plans">subscribe to an individual plan</a> first, or
          <a href="/signup/organization"> create a new organization</a> directly.
        </Alert>
      </Container>
    );
  }

  return (
    <div className={styles['upgrade-container']}>
      <Container maxWidth="md">
        <header className={styles['upgrade-header']}>
          <h1 className={styles['upgrade-title']}>
            Upgrade to Organization Plan
          </h1>
          <p className={styles['upgrade-description']}>
            Migrate your data and start collaborating with your team
          </p>
        </header>

        <Alert severity="info" className={styles['upgrade-notice']}>
          <strong>Migration Process:</strong> Your individual subscription will be canceled,
          and you'll be upgraded to an organization plan. You can select which data to migrate.
        </Alert>

        <Box className={styles['stepper-wrapper']}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            className={styles['stepper']}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    classes: {
                      root: styles['step-icon'],
                      active: styles['step-icon-active'],
                      completed: styles['step-icon-completed']
                    }
                  }}
                >
                  <span className={styles['step-label']}>
                    {label}
                  </span>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box
          className={styles['step-content']}
          role="region"
          aria-label={`Step ${activeStep + 1} of ${steps.length}: ${steps[activeStep]}`}
        >
          {getStepContent(activeStep)}
        </Box>
      </Container>
    </div>
  );
};

export default IndividualToOrgUpgrade;
